import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useUploadAttachment } from "@/shared/api/attachments";
import { modelKeys } from "@/shared/api/models";
import { ApiError } from "@/shared/api/errors";
import { useSettings } from "@/shared/api/settings";
import { REGENERATE_REPEATS_ACTIONS, useRegenerateRun, useStartRun, useStopRun } from "@/shared/api/runs";
import {
  drawsSomething, lastMessageThrough, summaryEndId, FRESH_THREAD, textOf, threadKeys, toMessages, turnId, UNFINISHED, useThreadMessages, useThreadRuns,
  useVoteOnAnswer,
} from "@/shared/api/threads";
import useRunStream from "./useRunStream";
import type { ApiRun, ApiThread, Chat, ContextAgent, Message, RunStarted } from "@/shared/types";
import type { ChatSource, PendingReply } from "../types";

/* A chat with a server agent: the thread's messages from the API, the turn in
   flight from its stream.

   What is on screen is always the server's, plus two things it cannot know
   yet: the message you just sent (the worker writes it down when it picks the
   turn up, not when `POST /runs` answers), and the turn being written. Both go
   when the refetch after `run.end` brings the real rows — the turn under the
   same key (`turnId`), so the screen keeps its place and what is open.

   A run can pause on the person: a question it asked, or a call to approve. It
   is answered by posting to the thread (`decision` for an approval), and the
   same run goes on — so what they type while it waits is its answer, not a new
   turn. A stopped or failed run is never carried on: Continue and Retry send
   "Continue." as a new turn that reads the transcript.

   Regenerate writes the newest answer again: what its run wrote leaves the server's
   transcript, and here at once (`sent.replaces`); the message it answered stays, the new
   run's now. Only your own message's answer, and one that did things is asked about first.

   A channel conversation goes on without anyone here: each message on
   Telegram, Slack or WhatsApp starts a turn. So while none is live it is
   re-read, to find the next turn and follow its stream like any other. */

/** Ceiling: each re-read fetches every page read so far; a channel conversation
    scrolled far back would want `after_seq` reads of what is new. */
const CHANNEL_REFRESH_MS = 5_000;

/** What Continue and Retry send: a new turn that picks up from the transcript. */
const CONTINUE = "Continue.";

interface Sent {
  /** What they said; null for a regenerate with no message of theirs above the answer. */
  message: Message | null;
  /** The thread it went to; null until `POST /runs` names a new one. */
  threadId: string | null;
  /** The last seq on screen when it was sent — a user row after it is its echo. */
  afterSeq: number;
  /** A regenerate's: the run whose answer is no longer the transcript's (its message is the new run's). */
  replaces?: string;
}

/** `about`: the agent Builder's picker names, sent with every new turn this chat starts (a
    message, Continue, Retry, an edit) and shown on its echo; never with the answer to a
    paused run. */
export default function useServerChat(
  agentId: string | null,
  chat: Chat | null,
  onChatStarted?: (threadId: string) => void,
  about: ContextAgent | null = null,
): ChatSource {
  const qc = useQueryClient();
  const threadId = chat?.id ?? null;
  const refresh = chat?.via ? CHANNEL_REFRESH_MS : 0;
  const { data: runs } = useThreadRuns(threadId, refresh);
  const latest = runs?.[0] ?? null;
  // The run the screen follows: the oldest one still to finish — a thread runs one at
  // a time and queues the rest behind it — or else the one that waits on a person.
  const watched = runs?.findLast((run) => UNFINISHED.includes(run.status)) ?? runs?.find((run) => run.status === "pending") ?? null;
  const waiting = watched?.status === "pending";
  /** The run whose stream draws the turn in flight. */
  const followed = waiting ? null : watched;
  const following = !!followed;
  const transcript = useThreadMessages(threadId, following ? 0 : refresh);
  const { rows } = transcript;
  // A run whose stream ended keeps drawing its turn from the stream until a transcript read
  // after the end lands: `run.ended` on the socket re-reads the runs beside the messages, and
  // the runs come back first, which would blank the turn until the messages do (DEF-401).
  const [ended, setEnded] = useState<{ runId: string; at: number } | null>(null);
  const ending = !followed && ended && transcript.readAt < ended.at
    ? runs?.find((run) => run.id === ended.runId && run.status !== "pending") ?? null
    : null;
  /** The run whose turn is drawn from its stream: the one followed, else the one handing over. */
  const drawn = followed ?? ending;
  const start = useStartRun();
  const reply = useStartRun();
  const upload = useUploadAttachment();
  const stopRun = useStopRun();
  const regen = useRegenerateRun();
  const me = useSettings().data?.user.id; // whose messages may be regenerated here
  const voting = useVoteOnAnswer();
  const [sent, setSent] = useState<Sent | null>(null);

  const streamed = watched ?? ending;
  const stream = useRunStream(streamed?.id ?? null, streamed?.attempt ?? 0, waiting, async () => {
    if (followed) setEnded({ runId: followed.id, at: Date.now() });
    // The saved turn before the status, so the live one is replaced rather than blinking out.
    await qc.invalidateQueries({ queryKey: threadKeys.messages(threadId!) });
    setSent(null);
    await qc.invalidateQueries({ queryKey: threadKeys.runs(threadId!) });
    qc.invalidateQueries({ queryKey: threadKeys.list(agentId!) });
    // a chat opened by its link reads its thread on its own: a summary made in this run
    // moves where its divider goes
    qc.invalidateQueries({ queryKey: threadKeys.one(threadId!) });
    // A run can change its connection: a 401 marks the key invalid, a 404 takes the
    // model off it, an answer heals `unavailable`. The picker should say so now.
    qc.invalidateQueries({ queryKey: modelKeys.all });
  });

  const live = following || start.isPending;
  // What the run asks is the run's own (`pending`): the card shows as soon as the runs
  // are read, on a reload too, and goes the moment the run goes on.
  const paused = waiting ? { asks: watched.pending, answering: reply.isPending } : null;
  const resumable = !live && !waiting && (latest?.status === "halted" || latest?.status === "error");

  // The turn in flight is drawn from its stream; its rows join the transcript once it ends.
  // Read once per transcript, so the saved turns stay the same objects while the live one streams.
  const drawnId = drawn?.id;
  const replaced = sent?.replaces;
  const saved = useMemo(
    () => toMessages(rows.filter((row) => row.role === "user" || (row.run_id !== replaced && row.run_id !== drawnId))),
    [rows, drawnId, replaced],
  );
  let messages = [...saved];
  const pausedCall = paused?.asks?.call.id;
  if (pausedCall) messages = withoutPausedCall(messages, pausedCall);
  const echoed = sent && rows.some((row) => row.role === "user" && row.seq > sent.afterSeq);
  if (sent?.message && sent.threadId === threadId && !echoed) messages.push(sent.message);
  // A turn not sent from here — a channel message, or one sent before this chat
  // was opened — shows its question from the stream until the transcript has it.
  const asked = !sent && drawn ? stream.asked : null;
  if (asked && !rows.some((row) => row.role === "user" && row.run_id === drawn?.id && row.content === asked)) {
    const about = stream.askedAbout;
    messages.push({ id: `asked:${drawn?.id}`, role: "user", text: asked, ...(about ? { contextAgent: about } : {}) });
  }
  // A turn sent from elsewhere whose question is not on screen yet, neither its row nor the
  // stream's: until it is, the newest person message here is the one before it.
  const unasked = !!followed && !sent && !asked && !rows.some((row) => row.role === "user" && row.run_id === followed.id);
  if (drawn) messages.push({ id: turnId(drawn.id), role: "agent", parts: stream.parts, text: textOf(stream.parts) });
  if (!live && latest?.status === "error") {
    messages.push({ id: `error:${latest.id}`, role: "agent", error: { title: "The agent hit an error", detail: latest.error ?? "The run failed." } });
  } else if (stream.status === "failed") {
    messages.push({ id: "error:stream", role: "agent", error: { title: "Lost the live answer", detail: `${stream.failure} — reload to read what was saved.` } });
  }

  /** The answer to the run that waits. It goes on as the same run: marked queued at
      once, so its stream is read on from the pause without waiting for a re-read. Resolves
      whether it went. */
  const answer = async ({ attachment_ids, ...said }: PendingReply & { attachment_ids?: string[] }) => {
    if (!agentId || !threadId || !watched) return false;
    const runId = watched.id;
    return reply.mutateAsync({ agent_id: agentId, thread_id: threadId, ...said, ...(attachment_ids?.length ? { attachment_ids } : {}) }).then(
      (started) => {
        if (started.how === "continued") {
          qc.setQueryData<ApiRun[]>(threadKeys.runs(threadId), (old) =>
            old?.map((run) => (run.id === runId ? { ...run, status: "queued", pending: null } : run)),
          );
        }
        return true;
      },
      () => false, // the toast says why
    );
  };

  /** Resolves whether the message went: false for an upload or a run the API refused. */
  const send = async (message: Message): Promise<boolean> => {
    const files = (message.attachments ?? []).flatMap((a) => (a.file ? [a.file] : []));
    if (!agentId || (!message.text && !files.length)) return false;
    // the API wants words with every turn; files alone say what they are
    const text = message.text || `Attached: ${files.map((f) => f.name).join(", ")}`;
    // While a run waits on this person, what they type is its answer: no new turn, so no echo.
    const answering = !!paused;
    const echo: Message = { ...message, text, ...(about ? { contextAgent: about } : {}) };
    if (!answering) setSent({ message: echo, threadId, afterSeq: rows.at(-1)?.seq ?? 0 });
    let ids: string[];
    try {
      ids = (await Promise.all(files.map((file) => upload.mutateAsync(file)))).map((f) => f.id);
    } catch (err) {
      console.warn("upload failed; the message was not sent", err); // the mutation cache toasts why
      setSent(null);
      return false;
    }
    if (answering) return answer({ text, attachment_ids: ids });
    // Awaited here, not `mutate`'s callbacks: those are dropped once the chat unmounts, and a
    // person who sends and opens Settings before the answer must still find the chat it started.
    let started: RunStarted;
    try {
      started = await start.mutateAsync({
        agent_id: agentId, text, ...(threadId ? { thread_id: threadId } : {}), ...(ids.length ? { attachment_ids: ids } : {}),
        ...(about ? { context_agent_id: about.id } : {}),
      });
    } catch {
      setSent(null); // the toast says why; the words go back to the composer (`ChatPane`)
      return false;
    }
    setSent((s) => s && { ...s, threadId: started.thread_id });
    if (threadId) return true;
    // The list refetch is on its way; this puts the new thread in it now,
    // so opening it does not flash the empty state.
    const opened: ApiThread = {
      ...FRESH_THREAD,
      id: started.thread_id,
      agent_id: agentId,
      title: text.slice(0, 80),
      created_by: null,
      channel_id: null,
      via: null,
      last_message_at: null,
      created_at: started.started_at,
    };
    qc.setQueryData<ApiThread[]>(threadKeys.list(agentId), (old = []) => [opened, ...old.filter((t) => t.id !== opened.id)]);
    onChatStarted?.(started.thread_id);
    return true;
  };

  // Regenerate goes under the newest answer once its run ended and nothing else is on its way
  // here, not under a failed run's line (Retry is there) — only when that run wrote the
  // transcript's last row (a branch's copied answers are its source's) and the message it
  // answers is yours: the new run is its sender's, on their accounts.
  const last = messages.at(-1);
  const prompt = latest && rows.findLast((row) => row.role === "user" && row.run_id === latest.id);
  const regenerable = !live && !waiting && !regen.isPending && !(sent && sent.threadId === threadId) &&
    latest && latest.status !== "error" && rows.at(-1)?.run_id === latest.id &&
    !!me && prompt?.author_id === me && last?.role === "agent" && !last.error ? last.id : null;

  const regenerate = async (messageId: string, repeatActions = false): Promise<string[] | null> => {
    if (!threadId || !latest || messageId !== regenerable) return null;
    try {
      await regen.mutateAsync({ runId: latest.id, repeatActions });
    } catch (err) {
      if (err instanceof ApiError && err.code === REGENERATE_REPEATS_ACTIONS) return (err.details ?? []).map((d) => String(d.tool));
      toast.error(err instanceof Error ? err.message : "The answer wasn't regenerated.");
      // a teammate may have gone on in this conversation since it was read: read it again
      qc.invalidateQueries({ queryKey: threadKeys.messages(threadId) });
      qc.invalidateQueries({ queryKey: threadKeys.runs(threadId) });
      return null;
    }
    // the old answer goes at once; their message stays, the new run's now, then that run is followed
    setSent({ message: null, threadId, afterSeq: rows.at(-1)?.seq ?? 0, replaces: latest.id });
    await qc.invalidateQueries({ queryKey: threadKeys.runs(threadId) });
    qc.invalidateQueries({ queryKey: threadKeys.list(agentId!) });
    return null;
  };

  const resume = () => {
    if (resumable) void send({ id: `u-${Date.now()}`, role: "user", text: CONTINUE });
  };

  const atSeq = chat?.branchedFrom?.atSeq;
  const branchedFrom = chat?.branchedFrom && atSeq !== undefined ? { ...chat.branchedFrom, atMessageId: lastMessageThrough(rows, atSeq) } : chat?.branchedFrom;

  return {
    messages,
    branchedFrom,
    summaryEndId: summaryEndId(rows, chat?.summaryUptoSeq),
    turn: followed ? { id: turnId(followed.id), startedAt: followed.started_at, quiet: stream.quiet } : null,
    older: transcript.older,
    read: transcript.read && !unasked,
    live,
    stopped: resumable && latest?.status === "halted",
    paused,
    send,
    answer,
    stop: () => watched && stopRun.mutate(watched.id),
    resume,
    retry: resume,
    regenerable,
    regenerate,
    edit: (_id, text) => send({ id: `u-${Date.now()}`, role: "user", text }),
    vote: (answerId, vote) => {
      if (threadId) voting.mutate({ threadId, answerId, vote });
    },
  };
}

/** The transcript while a run waits on a call: the call is the card under the composer, not
    a step that looks stopped — and a turn that only made that call shows nothing at all,
    rather than its avatar alone. */
export function withoutPausedCall(messages: Message[], callId: string): Message[] {
  return messages
    .map((m) => (m.parts ? { ...m, parts: m.parts.filter((p) => p.kind !== "tool" || p.step.id !== callId) } : m))
    .filter((m) => m.role !== "agent" || !!(m.text || m.blocks?.length || m.parts?.some(drawsSomething) || m.error));
}
